import { useMemo } from 'react';
import { Card, Row, Col, List, Collapse, Space } from 'antd';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  HelpCategory,
  helpCategories,
  type HelpTopic,
} from '@/constants/helpContent';

interface HelpCategoryListProps {
  topics: readonly HelpTopic[];
  onSelectTopic?: (topicId: string) => void;
}

/**
 * ヘルプカテゴリ一覧コンポーネント
 *
 * デスクトップ: カード形式のグリッド表示
 * モバイル: アコーディオン形式
 */
export function HelpCategoryList({ topics, onSelectTopic }: HelpCategoryListProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const groupedTopics = useMemo(() => {
    return Object.values(HelpCategory)
      .map((category) => ({
        category,
        topics: topics.filter((t) => t.category === category),
      }))
      .filter((g) => g.topics.length > 0);
  }, [topics]);

  // モバイル表示（アコーディオン）
  if (isMobile) {
    return (
      <Collapse
        accordion
        items={groupedTopics.map(({ category, topics: categoryTopics }) => ({
          key: category,
          label: (
            <Space size="small">
              <span style={{ fontSize: '20px' }}>{helpCategories[category].icon}</span>
              <span style={{ fontWeight: 500 }}>{helpCategories[category].label}</span>
            </Space>
          ),
          children: (
            <List
              dataSource={categoryTopics}
              renderItem={(topic) => (
                <List.Item
                  onClick={() => onSelectTopic?.(topic.id)}
                  style={{ cursor: onSelectTopic ? 'pointer' : 'default', padding: '8px 0' }}
                >
                  {onSelectTopic ? (
                    <span style={{ color: '#1890ff' }}>{topic.title}</span>
                  ) : (
                    <Link to={`/help/${topic.id}`}>{topic.title}</Link>
                  )}
                </List.Item>
              )}
            />
          ),
        }))}
      />
    );
  }

  // デスクトップ表示（カードグリッド）
  return (
    <Row gutter={[24, 24]}>
      {groupedTopics.map(({ category, topics: categoryTopics }) => (
        <Col key={category} xs={24} sm={12} lg={8}>
          <Card
            title={
              <Space size="small">
                <span style={{ fontSize: '24px' }}>{helpCategories[category].icon}</span>
                <span>{helpCategories[category].label}</span>
              </Space>
            }
            variant="borderless"
            style={{ height: '100%' }}
            styles={{ header: { backgroundColor: '#fafafa' } }}
          >
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
              {helpCategories[category].description}
            </p>
            <List
              dataSource={categoryTopics.slice(0, 5)}
              size="small"
              renderItem={(topic) => (
                <List.Item style={{ padding: '4px 0' }}>
                  {onSelectTopic ? (
                    <a onClick={() => onSelectTopic(topic.id)} style={{ cursor: 'pointer' }}>
                      {topic.title}
                    </a>
                  ) : (
                    <Link to={`/help/${topic.id}`}>{topic.title}</Link>
                  )}
                </List.Item>
              )}
            />
            {/* カテゴリページは未実装のため、コメントアウト */}
            {/* categoryTopics.length > 5 && (
              <Link to={`/help/${category}`} style={{ fontSize: '14px', marginTop: '8px', display: 'block' }}>
                すべて表示 →
              </Link>
            ) */}
          </Card>
        </Col>
      ))}
    </Row>
  );
}
